import React from "react";
import { useLocation } from "react-router-dom";
import {
  Dashboard,
  AdminPanelSettings,
  Policy,
  Assessment,
  Business,
  People,
  PersonAdd,
  School,
  Email,
  AlternateEmail,
  MarkEmailReadOutlined,
  AccountBalanceWallet,
  BarChart,
  TrendingUp,
  TableChart,
  Receipt,
  Description,
  CheckCircle,
  QueryStats,
  Subject,
  Groups,
  ContactMail,
  MenuBook,
  ReceiptLong,
  Schedule,
  PersonOutline,
  EventBusy,
  HowToReg,
  EventNote,
  CardMembership,
  Layers,
  EventAvailable,
  Payments,
  Discount,
  CompareArrows,
  Assignment,
  LocalShipping,
  ContactPhone,
  BadgeOutlined,
  FamilyRestroomOutlined,
  Tune,
  PaletteOutlined,
  RecentActorsOutlined,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { useSubscription } from "../../context/SubscriptionContext";
import { useModulePermissions } from "../../hooks/useModulePermissions";
import { UserRole, NavigationItem } from "../../types";
import { useFinanceSetupItems } from "../finance/setup/useFinanceSetupItems";

/** The sidebar's sections, in order. The first has no heading. */
export const NAV_AREAS = ["", "People", "Academics", "Management", "Platform"] as const;
export type NavArea = typeof NAV_AREAS[number];

export interface NavEntry extends NavigationItem {
  area: NavArea;
  submenu?: NavigationItem[];
  /** Pages outside the submenu that still belong to this group. Defaults to its path. */
  scope?: (pathname: string) => boolean;
}

/** One page anyone can jump to, with where it sits in the sidebar. */
export interface NavPage {
  title: string;
  path: string;
  icon?: React.ReactNode;
  /** The group and section it sits under, empty for a top-level page. */
  crumbs: string[];
  trail: string;
}

export const isUnder = (pathname: string, base: string) =>
  pathname === base || pathname.startsWith(base + "/");

// Fee reports live under Reports, so they don't light up Finance.
const isFinancePath = (path: string) =>
  (path.startsWith("/app/fees") && !path.startsWith("/app/fees/reports")) ||
  path.startsWith("/app/accounts") ||
  path.startsWith("/app/finance");

/**
 * Every page this user may open, by role, permission and subscription
 * feature, plus which page and group the current URL belongs to. The
 * sidebar and the page search both read it, so they never disagree.
 */
export const useNavigation = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { hasFeature } = useSubscription();
  const { canRead, isTeacher } = useModulePermissions();
  const financeSetupItems = useFinanceSetupItems();

  const items: NavEntry[] = [];
  const leaf = (title: string, icon: React.ReactElement, path: string, section?: string): NavigationItem =>
    ({ title, icon, path, section, requiredRoles: [] });

  if (canRead("dashboard")) {
    items.push({ ...leaf("Dashboard", <Dashboard />, "/app/dashboard"), area: "" });
  }

  // ── People ────────────────────────────────────────────────────────────────
  if (hasFeature("admissions") && canRead("admissions")) {
    // One umbrella for the cycles, the families waiting for a future
    // admission, and the cross-cycle enrollment handover — each one click
    // away rather than buried in a cycle's tabs.
    items.push({
      ...leaf("Admissions", <PersonAdd />, "/app/admissions"),
      area: "People",
      submenu: [
        leaf("Cycles", <PersonAdd />, "/app/admissions"),
        leaf("Enquiries", <ContactPhone />, "/app/admissions/enquiries"),
        leaf("Enrollment handover", <LocalShipping />, "/app/admissions/handover"),
      ],
    });
  }
  // The lifecycle continuation: prospective → admitted → enrolled per session.
  if (hasFeature("academic_sessions") && canRead("enrollments")) {
    items.push({ ...leaf("Enrollments", <Groups />, "/app/enrollments"), area: "People" });
  }
  // "Type a GR, see everything". Gated by `students:read` because it opens
  // the full profile incl. identity, fees and letters.
  if (canRead("students")) {
    items.push({ ...leaf("Student lookup", <BadgeOutlined />, "/app/students/lookup"), area: "People" });
  }
  // Households with their children and guardians.
  if (canRead("people")) {
    items.push({ ...leaf("Families", <FamilyRestroomOutlined />, "/app/families"), area: "People" });
  }
  if (canRead("users")) {
    items.push({ ...leaf("Employees", <People />, "/app/employees"), area: "People" });
  }
  if (hasFeature("id_cards")) {
    const cards: NavigationItem[] = [];
    if (canRead("id_cards_students")) cards.push(leaf("Student cards", <School />, "/app/id-cards/students"));
    if (canRead("id_cards_staff")) cards.push(leaf("Staff cards", <People />, "/app/id-cards/staff"));
    if (canRead("id_card_designs")) cards.push(leaf("Designs", <PaletteOutlined />, "/app/id-cards/designs"));
    if (cards.length > 0) {
      items.push({ ...leaf("ID Cards", <RecentActorsOutlined />, "/app/id-cards"), area: "People", submenu: cards });
    }
  }
  if (hasFeature("leaves")) {
    const leaves = [
      leaf("My Leaves", <PersonOutline />, "/app/leaves/my-leaves"),
      leaf("Request Leave", <EventNote />, "/app/leaves/requests"),
    ];
    if (canRead("leave_requests")) leaves.push(leaf("Approval", <HowToReg />, "/app/leaves/approval"));
    items.push({ ...leaf("Leaves", <EventBusy />, "/app/leaves"), area: "People", submenu: leaves });
  }

  // ── Academics ─────────────────────────────────────────────────────────────
  if (hasFeature("academic_sessions") && canRead("sessions")) {
    items.push({ ...leaf("Academic Sessions", <EventAvailable />, "/app/settings/sessions"), area: "Academics" });
  }
  if (hasFeature("attendance")) {
    const attendance = [leaf("My Attendance", <PersonOutline />, "/app/attendance/my-attendance")];
    if (canRead("student_attendance")) attendance.push(leaf("Students", <School />, "/app/attendance/students"));
    if (canRead("employee_attendance")) attendance.push(leaf("Employees", <People />, "/app/attendance/employees"));
    if (canRead("attendance_summary_students") || canRead("attendance_summary_employees")) {
      attendance.push(leaf("Summary", <Assignment />, "/app/attendance/summary"));
    }
    items.push({ ...leaf("Attendance", <CheckCircle />, "/app/attendance"), area: "Academics", submenu: attendance });
  }
  if (hasFeature("timetable")) {
    const timetable: NavigationItem[] = [];
    // Only Teachers have a teaching schedule.
    if (isTeacher) timetable.push(leaf("My Timetable", <PersonOutline />, "/app/timetable/my-timetable"));
    if (canRead("timetable")) timetable.push(leaf("Timetable", <Schedule />, "/app/timetable/board"));
    if (canRead("timetable_config") || canRead("rooms")) timetable.push(leaf("Setup", <Tune />, "/app/timetable/setup"));
    if (timetable.length > 0) {
      items.push({ ...leaf("Timetable", <Schedule />, "/app/timetable"), area: "Academics", submenu: timetable });
    }
  }
  if (hasFeature("examination")) {
    const exams: NavigationItem[] = [];
    if (canRead("create_exam")) exams.push(leaf("Manage Exams", <Assessment />, "/app/examination/manage"));
    if (canRead("enter_marks")) exams.push(leaf("Enter Marks", <TableChart />, "/app/examination/marks"));
    if (canRead("results")) exams.push(leaf("Results", <TableChart />, "/app/examination/results"));
    if (canRead("exam_attendance_sheets")) exams.push(leaf("Attendance Sheets", <HowToReg />, "/app/examination/attendance-sheets"));
    if (exams.length > 0) {
      items.push({ ...leaf("Examination", <Assessment />, "/app/examination"), area: "Academics", submenu: exams });
    }
  }
  if (hasFeature("question_bank")) {
    const papers: NavigationItem[] = [];
    if (canRead("question_bank")) papers.push(leaf("Question Bank", <MenuBook />, "/app/question-paper/bank"));
    if (canRead("generate_question_paper")) papers.push(leaf("Paper Builder", <Description />, "/app/question-paper/builder"));
    if (papers.length > 0) {
      items.push({ ...leaf("Question Paper", <MenuBook />, "/app/question-paper"), area: "Academics", submenu: papers });
    }
  }
  if (hasFeature("analysis")) {
    const analysis: NavigationItem[] = [];
    if (canRead("student_progress")) analysis.push(leaf("Student Progress", <School />, "/app/analysis/student-progress"));
    if (canRead("subject_performance")) analysis.push(leaf("Subject Performance", <Subject />, "/app/analysis/subject-performance"));
    if (canRead("class_performance")) analysis.push(leaf("Class Performance", <Groups />, "/app/analysis/class-performance"));
    if (analysis.length > 0) {
      items.push({ ...leaf("Analysis", <QueryStats />, "/app/analysis"), area: "Academics", submenu: analysis });
    }
  }

  // ── Management ────────────────────────────────────────────────────────────
  // Finance — one umbrella for every money screen and its setup, grouped
  // the way an accounts office works. Each item keeps its own permission.
  const finance: NavigationItem[] = [];
  const feesSection = "Fees";
  if (hasFeature("fees") && canRead("fees")) {
    // Collection is the daily home — top of the list.
    finance.push(
      leaf("Collection", <Payments />, "/app/fees/collection", feesSection),
      leaf("Challans", <Receipt />, "/app/fees/invoices", feesSection),
      leaf("Concessions", <Discount />, "/app/fees/concessions", feesSection),
      leaf("Reconciliation", <CompareArrows />, "/app/fees/reconciliation", feesSection),
      leaf("Assignments", <HowToReg />, "/app/fees/assignments", feesSection),
    );
  }
  if (hasFeature("fees") && canRead("fee_plans")) {
    finance.push(leaf("Fee plans", <Description />, "/app/fees/plans", feesSection));
  }
  const moneySection = "Income & expenses";
  if (hasFeature("letters") && canRead("fines")) {
    finance.push(leaf("Fines", <Payments />, "/app/accounts/fines", moneySection));
  }
  // Revenue — the money-in side. No approval flow; entries post at once.
  if (hasFeature("expenses") && canRead("revenues")) {
    finance.push(leaf("Revenue", <Payments />, "/app/accounts/revenues", moneySection));
  }
  if (hasFeature("expenses") && canRead("expenses")) {
    finance.push(leaf("My expenses", <Receipt />, "/app/accounts/expenses", moneySection));
  }
  if (hasFeature("expenses") && canRead("expense_approval")) {
    finance.push(leaf("Expense approvals", <Receipt />, "/app/accounts/expense-approvals", moneySection));
  }
  const booksSection = "Books";
  if (canRead("fee_refunds") || canRead("fee_writeoffs") || canRead("finance_journal") || canRead("finance_journal_approve") || canRead("cash_count")) {
    finance.push(leaf("Approvals", <CheckCircle />, "/app/finance/approvals", booksSection));
  }
  if (hasFeature("expenses") && canRead("finance_journal")) {
    finance.push(
      leaf("Journal", <MenuBook />, "/app/finance/journal", booksSection),
      leaf("Ledger", <ReceiptLong />, "/app/finance/ledger", booksSection),
      leaf("Trial balance", <MenuBook />, "/app/finance/trial-balance", booksSection),
      leaf("Statements", <Assessment />, "/app/finance/statements", booksSection),
    );
  }
  // Every rupee in and out of cash and bank, from the books.
  if (hasFeature("expenses") && canRead("ledger")) {
    finance.push(leaf("Cash & bank", <AccountBalanceWallet />, "/app/finance/cash-bank", booksSection));
  }
  if (financeSetupItems.length > 0) {
    finance.push(leaf("Setup", <Tune />, "/app/finance/setup", booksSection));
  }
  if (finance.length > 0) {
    items.push({
      ...leaf("Finance", <AccountBalanceWallet />, finance[0].path!),
      area: "Management",
      submenu: finance,
      scope: isFinancePath,
    });
  }
  if (hasFeature("reports")) {
    const reports: NavigationItem[] = [];
    if (canRead("student_reports")) {
      reports.push(
        leaf("Students", <School />, "/app/reports/student-reports"),
        leaf("Exam Reports", <Assessment />, "/app/reports/exam-reports"),
      );
    }
    if (canRead("finance_reports")) reports.push(leaf("Finance", <TrendingUp />, "/app/reports/finance"));
    if (hasFeature("fees") && canRead("fee_reports")) reports.push(leaf("Fees", <TrendingUp />, "/app/fees/reports"));
    if (reports.length > 0) {
      items.push({ ...leaf("Reports", <BarChart />, "/app/reports"), area: "Management", submenu: reports });
    }
  }
  // Students are managed under Enrollments; the old Administration →
  // Students path redirects there.
  const administration: NavigationItem[] = [];
  if (hasFeature("letters") && canRead("letters")) administration.push(leaf("Letters", <Email />, "/app/administration/letters"));
  if (hasFeature("events") && canRead("events")) administration.push(leaf("Events", <EventNote />, "/app/administration/events"));
  if (administration.length > 0) {
    items.push({ ...leaf("Administration", <AdminPanelSettings />, "/app/administration"), area: "Management", submenu: administration });
  }
  // Emails to families — what went out, and how the school sends them.
  if (hasFeature("email") && canRead("email")) {
    items.push({
      ...leaf("Email", <AlternateEmail />, "/app/email"),
      area: "Management",
      submenu: [
        leaf("Sent emails", <MarkEmailReadOutlined />, "/app/email"),
        leaf("Setup", <Tune />, "/app/email/setup"),
      ],
    });
  }
  if (hasFeature("policies") && canRead("policies")) {
    items.push({ ...leaf("Policies", <Policy />, "/app/policies"), area: "Management" });
  }
  if (hasFeature("sops") && canRead("sops")) {
    items.push({ ...leaf("SOPs", <Description />, "/app/sops"), area: "Management" });
  }

  // ── Platform (system admins) ──────────────────────────────────────────────
  if (user?.isSystemAdmin) {
    items.push(
      { ...leaf("Schools", <Business />, "/app/schools"), requiredRoles: [UserRole.SUPER_ADMIN], area: "Platform" },
      { ...leaf("Demo Requests", <ContactMail />, "/app/demo-requests"), area: "Platform" },
      {
        ...leaf("Subscriptions", <CardMembership />, "/app/subscriptions"),
        requiredRoles: [UserRole.SUPER_ADMIN],
        area: "Platform",
        submenu: [
          leaf("Plans", <Layers />, "/app/subscriptions/plans"),
          leaf("Manage", <Business />, "/app/subscriptions/manage"),
        ],
      },
    );
  }

  const areas = NAV_AREAS
    .map((label) => ({ label, items: items.filter((i) => i.area === label) }))
    .filter((a) => a.items.length > 0);

  const pages: NavPage[] = items.flatMap((i) =>
    i.submenu
      ? i.submenu.map((s) => {
          const crumbs = [i.title, s.section].filter((c): c is string => !!c);
          return { title: s.title, path: s.path!, icon: s.icon, crumbs, trail: crumbs.join(" · ") };
        })
      : [{ title: i.title, path: i.path!, icon: i.icon, crumbs: [], trail: i.area || "Home" }],
  );

  // The most specific page the URL sits on — so /app/admissions/handover
  // lights "Enrollment handover", not "Cycles" as well.
  const activePath =
    pages
      .map((p) => p.path)
      .filter((p) => isUnder(pathname, p))
      .sort((a, b) => b.length - a.length)[0] ?? null;

  const groups = items.filter((i) => i.submenu);
  const activeGroup =
    (groups.find((g) => g.submenu!.some((s) => s.path === activePath)) ??
      groups.find((g) => (g.scope ? g.scope(pathname) : isUnder(pathname, g.path!))))?.title ?? null;

  const activePage = pages.find((p) => p.path === activePath) ?? null;

  return { areas, pages, activePath, activePage, activeGroup };
};
